/**
 * MonthlyReflection — Phase 2B (Figma refresh, revision 2).
 *
 * Two-column layout:
 *   LEFT  — "How [prev month] went"
 *           2120's own factual read of the previous calendar month.
 *           This is authored server-side by the Phase 2D briefing
 *           composer (provenance 'H'). The frontend never composes this
 *           text — it renders what it receives.
 *
 *   RIGHT — "Biggest opportunity for [current month]"
 *           Also composer-authored. Provenance 'H'.
 *
 * CTAs:
 *   • "Reflect on [prev month]"     → opens the reflection drawer so the
 *                                     USER can add their own comments on
 *                                     the previous month's performance.
 *                                     Disabled if no reflection question
 *                                     is available.
 *   • "View previous reflections"   → deep-link into /reflections.
 *
 * Data sources (all pre-existing, no new backend calls in Phase 2B):
 *   priorMonthRecap  — { title?, body, provenance } | null
 *   opportunity      — { title, body, provenance }  | null
 *   question         — /api/questions?source=reflection
 *   latestAnswer     — /api/weekly-reflections/current (kept for future)
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight, Sparkles, CalendarDays } from 'lucide-react';

/** Return "June" for the previous calendar month, respecting locale. */
function prevMonthLabel() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString(undefined, { month: 'long' });
}

export default function MonthlyReflection({
  question = null,          // { id, question_text, subtext? }
  latestAnswer = null,      // reserved for future use
  priorMonthRecap = null,   // { title?, body, provenance } | null
  opportunity = null,       // { title, body, provenance }  | null
  loading = false,
  onOpenDrawer,
}) {
  const prevMonth    = prevMonthLabel();
  const currentMonth = new Date().toLocaleDateString(undefined, { month: 'long' });

  if (loading) {
    return (
      <Card data-testid="dashboard-monthly-reflection" className="border-border/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Monthly reflection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="dashboard-monthly-reflection" className="border-border/70">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5 text-muted-foreground" /> Monthly reflection
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          A quiet look back at {prevMonth}, and the single thing worth improving next.
        </p>
      </CardHeader>

      <CardContent>
        <div className="grid gap-6 md:grid-cols-2">
          {/* LEFT — 2120's factual read of the previous month */}
          <section
            className="rounded-xl bg-muted/50 border border-border/50 px-4 py-4"
            data-testid="reflection-prior-month-recap"
          >
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3" /> How {prevMonth} went
            </div>

            {priorMonthRecap ? (
              <>
                {priorMonthRecap.title && (
                  <p
                    className="font-fraunces text-lg leading-snug text-foreground mt-2"
                    data-testid="reflection-recap-title"
                  >
                    {priorMonthRecap.title}
                  </p>
                )}
                {priorMonthRecap.body && (
                  <p
                    className="text-sm text-muted-foreground mt-2 leading-relaxed"
                    data-testid="reflection-recap-body"
                  >
                    {priorMonthRecap.body}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-2" data-testid="reflection-recap-empty">
                A read of {prevMonth} will appear here once your data supports one.
              </p>
            )}
          </section>

          {/* RIGHT — Biggest opportunity for the current month */}
          <section
            className="rounded-xl bg-sage-light/40 border border-primary/10 px-4 py-4"
            data-testid="reflection-biggest-opportunity"
          >
            <div className="text-[11px] uppercase tracking-[0.14em] text-primary font-medium flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Biggest opportunity for {currentMonth}
            </div>
            {opportunity ? (
              <>
                <p
                  className="font-fraunces text-lg leading-snug text-foreground mt-2"
                  data-testid="reflection-opportunity-title"
                >
                  {opportunity.title}
                </p>
                {opportunity.body && (
                  <p
                    className="text-sm text-muted-foreground mt-2 leading-relaxed"
                    data-testid="reflection-opportunity-body"
                  >
                    {opportunity.body}
                  </p>
                )}
              </>
            ) : question ? (
              <>
                <p
                  className="font-fraunces text-lg leading-snug text-foreground mt-2"
                  data-testid="reflection-opportunity-fallback"
                >
                  {question.question_text}
                </p>
                {question.subtext && (
                  <p className="text-sm text-muted-foreground mt-2">{question.subtext}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-2" data-testid="reflection-opportunity-empty">
                A composed opportunity will appear here once your data supports one.
              </p>
            )}
          </section>
        </div>

        {/* CTA row — Reflect on prev month allows USER to add their own note */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button
            onClick={() => onOpenDrawer?.('reflection', question?.id)}
            disabled={!question}
            data-testid="reflection-cta-write"
          >
            Reflect on {prevMonth}
            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </Button>
          <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
            <Link to="/reflections" data-testid="reflection-cta-view-previous">
              View previous reflections
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
